import { useState } from "react";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import { buildContextoPropiedadIA } from "../../utils/buildContextoPropiedadIA";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadLabel } from "../../context/PropiedadIdiomaContext";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import { IDIOMA_FUENTE } from "../../constants/propiedadIdiomas";

export default function SeccionPublicacionWeb(props) {
  const {
    form, cambiar, cambiarDirecto, auxiliares, superficies = [], camposError = [],
    onTextosWebGenerados = null,
    soloLecturaIdioma = false,
    idiomaVista = IDIOMA_FUENTE,
  } = props;
  const [generando, setGenerando] = useState("");
  const t = usePropiedadLabel();
  const { tv } = usePropiedadUi(props);
  const hasError = (n) => camposError.includes(n);
  const { puede } = usePermiso();
  const puedeVerWebTitulo = puede("web_titulo", "ver");
  const puedeModificarWebTitulo = puede("web_titulo", "modificar") && !soloLecturaIdioma;
  const puedeVerDescripcionCorta = puede("descripcion_corta", "ver");
  const puedeModificarDescripcionCorta = puede("descripcion_corta", "modificar") && !soloLecturaIdioma;
  const puedeVerDescripcionLarga = puede("descripcion_larga", "ver");
  const puedeModificarDescripcionLarga = puede("descripcion_larga", "modificar") && !soloLecturaIdioma;
  const puedeVerWebGenerarIa = puede("web_generar_ia", "ver");

  const contextoPropiedad = () => buildContextoPropiedadIA(form, auxiliares, superficies);

  const nombresProhibidosUbicacion = () => {
    const out = [];
    const urb = (auxiliares?.urbanizacion || []).find((x) => String(x.id) === String(form.urbanizacion_id));
    const zona = (auxiliares?.zona || []).find((x) => String(x.id) === String(form.zona_id));
    if (urb?.nombre) out.push(urb.nombre);
    if (zona?.nombre) out.push(zona.nombre);
    return out;
  };

  const generarDescripcion = async (tipo) => {
    const ctx = contextoPropiedad();
    if (!ctx || ctx.length < 20) {
      await dialogs.alert(tv("Rellena más datos de la propiedad antes de generar la descripción (tipo, m², dormitorios, etc.)"));
      return;
    }
    setGenerando(tipo);
    try {
      const res = await api.post("/ia/descripcion", {
        contexto: ctx,
        tipo,
        idioma: "es",
        nombresProhibidos: nombresProhibidosUbicacion(),
        poblacionPermitida: form.poblacion_nombre || form.poblacion || null,
        m2_declarados_escritura: form.m2_declarados_escritura,
        m2_se_pueden_declarar: form.m2_se_pueden_declarar,
        m2_declaracion_quien_paga: form.m2_declaracion_quien_paga,
      });
      const texto = res.data.texto;
      if (tipo === "corta") {
        cambiarDirecto("web_desc_corta_es", texto);
        if (typeof onTextosWebGenerados === "function" && texto) {
          await onTextosWebGenerados({ web_desc_corta_es: texto });
        }
      } else {
        cambiarDirecto("web_desc_larga_es", texto);
        if (typeof onTextosWebGenerados === "function" && texto) {
          await onTextosWebGenerados({ web_desc_larga_es: texto });
        }
      }
    } catch (err) {
      const st = err?.response?.status;
      const msg =
        st === 502 || st === 503 || st === 504
          ? tv("El servidor no respondió a tiempo. Espera unos segundos y vuelve a intentarlo.")
          : err.response?.data?.error || err.message;
      await dialogs.alert(`${tv("Error al generar")}: ${msg}`);
    }
    setGenerando("");
  };

  const langLabel = idiomaVista === IDIOMA_FUENTE ? "Español" : idiomaVista.toUpperCase();

  return (
    <div>
      <h2 className="font-bold text-gray-700 mb-4 text-sm uppercase tracking-wide">{tv("Publicación Web")}</h2>
      {puedeVerWebGenerarIa && !soloLecturaIdioma && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button type="button" onClick={() => generarDescripcion("corta")} disabled={!!generando}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
            {generando === "corta" ? <span className="animate-spin">⏳</span> : "🤖"} {tv("Crear Descripción Corta")}
          </button>
          <button type="button" onClick={() => generarDescripcion("larga")} disabled={!!generando}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
            {generando === "larga" ? <span className="animate-spin">⏳</span> : "🤖"} {tv("Crear Descripción Larga")}
          </button>
        </div>
      )}
      <div className="flex flex-col gap-4">
        {puedeVerWebTitulo && (
        <div className="flex flex-col gap-1">
          <label className={`text-xs font-medium ${hasError("web_titulo") ? "text-red-600" : "text-gray-500"}`}>
            {t("web_titulo", "Título del Inmueble")} ({langLabel})
            {idiomaVista === IDIOMA_FUENTE && <span className="text-red-500"> *</span>}
          </label>
          <input
            id="campo-web_titulo"
            type="text"
            name="web_titulo"
            value={form.web_titulo || ""}
            onChange={cambiar}
            disabled={!puedeModificarWebTitulo}
            readOnly={soloLecturaIdioma}
            placeholder={t("web_titulo", "Título para publicación web")}
            className={`px-2 py-1.5 border rounded-lg text-sm bg-white ${hasError("web_titulo") ? "border-red-400" : "border-gray-300"}${puedeModificarWebTitulo ? "" : " opacity-60 cursor-not-allowed"}`}
            style={{ scrollMarginTop: 120 }}
          />
        </div>
        )}
        {puedeVerDescripcionCorta && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">
            {t("web_desc_corta_es", "Descripción Corta")} ({langLabel})
          </label>
          <textarea name="web_desc_corta_es" value={form.web_desc_corta_es || ""} onChange={cambiar} rows={3} disabled={!puedeModificarDescripcionCorta} readOnly={soloLecturaIdioma} placeholder={t("web_desc_corta_es", "Descripción corta...")} className={`px-2 py-1.5 border border-gray-300 rounded-lg text-sm resize-y bg-white${puedeModificarDescripcionCorta ? "" : " opacity-60 cursor-not-allowed"}`} />
        </div>
        )}
        {puedeVerDescripcionLarga && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">
            {t("web_desc_larga_es", "Descripción Larga")} ({langLabel})
          </label>
          <textarea id="campo-web_desc_larga_es" name="web_desc_larga_es" value={form.web_desc_larga_es || ""} onChange={cambiar} rows={6} disabled={!puedeModificarDescripcionLarga} readOnly={soloLecturaIdioma} placeholder={t("web_desc_larga_es", "Descripción detallada...")} className={`px-2 py-1.5 border border-gray-300 rounded-lg text-sm resize-y bg-white${puedeModificarDescripcionLarga ? "" : " opacity-60 cursor-not-allowed"}`} style={{ scrollMarginTop: 120 }} />
        </div>
        )}
      </div>
    </div>
  );
}
